import { Injectable, signal } from '@angular/core';
import { Observable, catchError, finalize, map, of, switchMap, tap } from 'rxjs';
import { HttpContext } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { HttpService } from '@services/http.service';
import { StorageHelper } from '@helpers/storage.helper';
import { EnvName } from '@enums/environment.enum';
import { BaseTokenResponse, LoginTokenResponse } from '../dto/authentication/token-response.dto';
import { Router } from '@angular/router';
import { LOADING_ENABLED } from '../interceptors/http-loading.interceptor';
import { CurrentUserRolesService } from './current-user-roles.service';
import { SessionNotificationService } from './session-notification.service';


@Injectable({
    providedIn: 'root'
})
export class AuthService {
    constructor(
        private http: HttpService,
        private router: Router,
        private currentUserRoles: CurrentUserRolesService,
        private sessionNotifications: SessionNotificationService
    ) {
        const currentToken = StorageHelper.getToken();
        if (currentToken?.accessToken && environment.envName !== EnvName.LOCAL) {
            this.sessionNotifications.connect();
            this.syncHangfireSession(currentToken).subscribe({ error: () => { } });
        }
    }
    private _BASE_URL: string = environment.apiBaseUrl;
    private _TABLE: string = "auth";

    private readonly _isLogIn = signal<boolean>(this.hasValidToken());
    /** Expose read-only signal to components */
    public readonly isLogIn = this._isLogIn.asReadonly();

    private hasValidToken(): boolean {
        const t = StorageHelper.getToken();
        return !!t?.accessToken;
    }
    private setLoggedInTokens(token: LoginTokenResponse | BaseTokenResponse): void {
        StorageHelper.setToken(token);
        this._isLogIn.set(true);
        if (environment.envName !== EnvName.LOCAL) this.sessionNotifications.connect();
    }

    // ---------------- API methods ----------------

    //Thực thi việc đăng nhập và lấy token
    signInPassword(phoneNumber: string, password: string): Observable<LoginTokenResponse> {
        StorageHelper.removeToken();
        this.currentUserRoles.clear();
        if (environment.envName == EnvName.LOCAL) {
            return this.http.get(`assets/mock-data/auth/login-success.json`)
                .pipe(tap(res => { if (res?.accessToken) this.setLoggedInTokens(res); }));
        }

        const data = {
            phoneNumber: phoneNumber,
            password: password
        };
        return this.http.post(`${this._BASE_URL}/${this._TABLE}/sign-in`, data)
            .pipe(tap(res => {
                if (res?.accessToken) {
                    this.setLoggedInTokens(res);
                }
            }), switchMap(res => this.syncHangfireSession(res)));
    }

    /** Refresh -> store new tokens -> keep isLogIn=true */
    refreshToken(token: string): Observable<BaseTokenResponse> {
        if (environment.envName == EnvName.LOCAL) {
            return this.http
                .get(`assets/mock-data/auth/refresh-token.json`)
                .pipe(tap(res => { if (res?.accessToken) this.setLoggedInTokens(res); }));
        }
        return this.http.post(`${this._BASE_URL}/${this._TABLE}/refresh-token`, { token })
           .pipe(
                tap(res => { if (res?.accessToken) this.setLoggedInTokens(res); }),
                switchMap(res => this.syncHangfireSession(res))
            );
    }

    private syncHangfireSession<T extends BaseTokenResponse>(response: T): Observable<T> {
        if (!response?.accessToken) return of(response);

        // The interceptor attaches the freshly stored bearer token. The API
        // validates it and returns an HttpOnly cookie scoped to /hangfire.
        return this.http.post(`${this._BASE_URL}/${this._TABLE}/hangfire-session`, {}, {
            withCredentials: true,
            context: new HttpContext().set(LOADING_ENABLED, false)
        }).pipe(
            map(() => response),
            // Cookie setup is optional for normal API sign-in. A failure here
            // must not keep the login flow pending or prevent the admin UI opening.
            catchError(() => of(response))
        );
    }

    //đăng xuất
    logout(urlLogin: string): void {
        const clearLocalSession = () => {
            void this.sessionNotifications.disconnect();
            StorageHelper.clearItems(true);
            this._isLogIn.set(false);
            this.router.navigate(['auth/login']);
        };

        if (environment.envName === EnvName.LOCAL) {
            clearLocalSession();
            return;
        }

        this.http.delete(`${this._BASE_URL}/${this._TABLE}/hangfire-session`, undefined, { withCredentials: true })
            .pipe(finalize(clearLocalSession))
            .subscribe({ error: () => { } });
    }
}
